import { ref, onMounted, onUnmounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/composables/useToast';

/**
 * Idle Timeout Composable
 * 
 * Automatically logs out users after a period of inactivity
 * Monitors user interactions (mouse, keyboard, touch, scroll)
 * 
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 240 = 4 hours)
 */
export function useIdleTimeout(timeoutMinutes = 240) {
  const authStore = useAuthStore();
  const toast = useToast();
  
  let idleTimer = null;
  let warningTimer = null;
  const showingWarning = ref(false);
  
  /**
   * Reset the idle timer on user activity
   */
  const resetTimer = () => {
    // Clear existing timers
    if (idleTimer) clearTimeout(idleTimer);
    if (warningTimer) clearTimeout(warningTimer);
    showingWarning.value = false;
    
    // Show warning 5 minutes before logout
    const warningTime = (timeoutMinutes - 5) * 60 * 1000;
    warningTimer = setTimeout(() => {
      if (authStore.isAuthenticated) {
        showingWarning.value = true;
        toast.warning('Your session will expire in 5 minutes due to inactivity', {
          timeout: 300000 // Show for 5 minutes
        });
      }
    }, warningTime);
    
    // Set main idle timer
    idleTimer = setTimeout(() => {
      if (authStore.isAuthenticated) {
        console.log('User idle timeout - logging out');
        toast.info('You have been logged out due to inactivity');
        authStore.logout();
      }
    }, timeoutMinutes * 60 * 1000);
  };
  
  /**
   * Activity handler
   */
  const handleActivity = () => {
    // Only reset if user is authenticated
    if (authStore.isAuthenticated) {
      resetTimer();
    }
  };
  
  /**
   * Initialize idle detection
   */
  const initialize = () => {
    // Events to monitor for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    
    // Start the initial timer
    resetTimer();
    
    console.log(`[IdleTimeout] Initialized with ${timeoutMinutes} minute timeout`);
  };
  
  /**
   * Cleanup function
   */
  const cleanup = () => {
    // Clear timers
    if (idleTimer) clearTimeout(idleTimer);
    if (warningTimer) clearTimeout(warningTimer);
    
    // Remove event listeners
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
    events.forEach(event => {
      document.removeEventListener(event, handleActivity, true);
    });
    
    console.log('[IdleTimeout] Cleaned up');
  };
  
  // Auto-initialize on mount and cleanup on unmount
  onMounted(() => {
    // Only initialize if user is authenticated
    if (authStore.isAuthenticated) {
      initialize();
    }
  });
  
  onUnmounted(() => {
    cleanup();
  });
  
  return {
    resetTimer,
    initialize,
    cleanup,
    showingWarning
  };
}

